import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Button } from "../../../../../design-system";
import {
  approveScheduledPost,
  cancelScheduledPost,
  updateScheduledPost,
} from "../../../../../database/community/scheduledPosts";
import { useMediaStudio } from "../../context/MediaStudioContext";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import SchedulePostDialog from "../../publish/SchedulePostDialog";
import ReviewCard, { ReviewDecision } from "./ReviewCard";
import {
  SectionRoot,
  SectionTitle,
  SectionSub,
  EmptyState,
  Muted,
  Thumb,
  Row,
  RowList,
  Card,
  CardTitle,
  CardHint,
  projectThumb,
} from "../shared";

// The approve queue: one draft at a time, keyboard driven. Approve flips the
// draft to scheduled (the MCP poller publishes it at the due time), Reject
// cancels it, Skip moves on. Edits save before approving.

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const ProgressText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const UpNextStrip = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const UpNextThumb = styled.img<{ $dim: boolean }>`
  width: 34px;
  height: 42px;
  object-fit: cover;
  border-radius: 6px;
  opacity: ${(p) => (p.$dim ? 0.45 : 1)};
  background: var(--color-surface-subtle);
`;

const ErrorNote = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const isEditableTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable
  );
};

export type ReviewQueueProps = {
  onNavigate: (path: string) => void;
};

export const ReviewQueue: React.FC<ReviewQueueProps> = ({ onNavigate }) => {
  const { communityId, posts, projects, connections } = useMediaStudio();
  const [index, setIndex] = useState(0);
  const action = useAsyncAction();
  const captionRef = useRef<HTMLTextAreaElement>(null);

  const drafts = posts.drafts;
  const clamped = Math.min(index, Math.max(0, drafts.length - 1));
  const current = drafts[clamped];
  const currentProject = current?.projectId
    ? projects.projectById.get(current.projectId)
    : undefined;

  // Composed projects that are ready but not yet queued anywhere.
  const queuedProjectIds = useMemo(
    () => new Set(posts.posts.map((p) => p.projectId).filter(Boolean)),
    [posts.posts]
  );
  const readyProjects = useMemo(
    () =>
      projects.projects.filter(
        (p) => p.status === "ready" && !queuedProjectIds.has(p.id)
      ),
    [projects.projects, queuedProjectIds]
  );

  const firstParty = {
    instagram: connections.flags.instagram,
    facebook: connections.flags.facebook,
    tiktok: connections.flags.tiktok,
    linkedin: connections.flags.linkedin,
    youtube: connections.flags.youtube,
  };

  const handleApprove = (decision: ReviewDecision) => {
    if (!communityId || !current) return;
    const id = current.id;
    action.run(
      async () => {
        if (decision.changed) {
          await updateScheduledPost(communityId, id, {
            caption: decision.caption,
            dueAt: decision.dueAtIso,
            platforms: decision.platforms,
          });
        }
        await approveScheduledPost(communityId, id);
        return "Approved. It goes out at the set time.";
      },
      { fallbackError: "Could not approve the post." }
    );
  };

  const handleReject = () => {
    if (!communityId || !current) return;
    const id = current.id;
    action.run(
      async () => {
        await cancelScheduledPost(communityId, id);
        return "Rejected. The draft was cancelled.";
      },
      { fallbackError: "Could not reject the post." }
    );
  };

  const handleReplaceMedia = (item: { mediaUrl: string }) => {
    if (!communityId || !current) return;
    const id = current.id;
    // Detach from the composed project so the picked media actually publishes
    // (the publisher uses the project's media whenever projectId is set).
    action.run(
      async () => {
        await updateScheduledPost(communityId, id, {
          mediaUrls: [item.mediaUrl],
          projectId: null,
        });
        return "Image replaced for this post.";
      },
      { fallbackError: "Could not replace the image." }
    );
  };

  const handleSkip = () => {
    if (drafts.length === 0) return;
    setIndex((i) => (i + 1) % drafts.length);
  };

  const handlePrev = () => {
    if (drafts.length === 0) return;
    setIndex((i) => (i - 1 + drafts.length) % drafts.length);
  };

  // Keyboard: A approve (as-is edits included), X reject, arrows navigate,
  // E focus caption. Disabled while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (!current) return;
      const key = e.key.toLowerCase();
      if (key === "arrowright") {
        e.preventDefault();
        handleSkip();
      } else if (key === "arrowleft") {
        e.preventDefault();
        handlePrev();
      } else if (key === "e") {
        e.preventDefault();
        captionRef.current?.focus();
      } else if (key === "x") {
        e.preventDefault();
        handleReject();
      } else if (key === "a") {
        e.preventDefault();
        // Approve with whatever is currently in the card's fields: click the
        // primary path through the card is not reachable here, so approve
        // as-queued (unedited) via the data layer.
        handleApprove({
          caption: current.caption,
          dueAtIso: current.dueAt || new Date().toISOString(),
          platforms: current.platforms as any,
          changed: false,
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, drafts.length, communityId]);

  return (
    <SectionRoot>
      <div>
        <SectionTitle>Review</SectionTitle>
        <SectionSub>
          Approve, tweak, or reject drafts before they go out. Approved posts
          publish automatically at their set time.
        </SectionSub>
      </div>

      {drafts.length === 0 ? (
        <>
          <EmptyState>
            <div style={{ fontSize: 26 }}>✅</div>
            <div>You're all caught up. No drafts waiting for review.</div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onNavigate("/media-studio/create")}
            >
              Create something new
            </Button>
          </EmptyState>

          {readyProjects.length > 0 && (
            <Card>
              <CardTitle>Ready to queue</CardTitle>
              <CardHint>
                These composed projects are finished but not queued yet. Queue
                one as a draft and it shows up here for review.
              </CardHint>
              <RowList>
                {readyProjects.slice(0, 6).map((p) => {
                  const thumb = projectThumb(p);
                  return (
                    <Row key={p.id}>
                      {thumb ? <Thumb src={thumb} alt="" /> : null}
                      <Muted style={{ color: "var(--color-text)" }}>
                        {p.title ||
                          (p.kind === "carousel" ? "Carousel" : "Reel")}
                      </Muted>
                      <span style={{ flex: 1 }} />
                      <SchedulePostDialog
                        communityId={communityId}
                        project={p}
                        firstParty={firstParty}
                        asDraft
                        triggerLabel="Queue as draft"
                      />
                    </Row>
                  );
                })}
              </RowList>
            </Card>
          )}
        </>
      ) : (
        <>
          <ProgressRow>
            <ProgressText>
              {clamped + 1} of {drafts.length}
            </ProgressText>
            <UpNextStrip>
              {drafts.slice(0, 8).map((p, i) => {
                const thumb = projectThumb(
                  p.projectId ? projects.projectById.get(p.projectId) : undefined
                );
                return thumb ? (
                  <UpNextThumb
                    key={p.id}
                    src={thumb}
                    alt=""
                    $dim={i !== clamped}
                    onClick={() => setIndex(i)}
                    style={{ cursor: "pointer" }}
                  />
                ) : null;
              })}
            </UpNextStrip>
            {action.notice && <Muted>{action.notice}</Muted>}
            {action.error && <ErrorNote>{action.error}</ErrorNote>}
          </ProgressRow>

          {current && (
            <ReviewCard
              key={current.id}
              post={current}
              project={currentProject}
              communityId={communityId}
              busy={action.busy}
              firstParty={firstParty}
              onApprove={handleApprove}
              onReject={handleReject}
              onSkip={handleSkip}
              onReplaceMedia={handleReplaceMedia}
              captionRef={captionRef}
            />
          )}
        </>
      )}
    </SectionRoot>
  );
};

export default ReviewQueue;
